import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AiFillEye, AiFillEyeInvisible, AiFillCloseCircle } from 'react-icons/ai';
import { useAppContext } from 'providers/AppProvider';
import { settings } from 'config';
import { handleValueDelete } from 'components/common/SunnyUtils';
import { systemAdminLogin } from 'hooks/CallApi';
import AuthCardLayout from 'layouts/AuthCardLayout';
import avatarImg_SA from 'assets/img/initech_ci.png';

/**
 * 시스템 관리자 로그인 컴포넌트
 * SSO 서버를 우회하여 시스템 관리자 계정으로 로그인
 */
const SystemAdminLogin = () => {
  const navigate = useNavigate();
  const { setConfig } = useAppContext();
  
  const [formData, setFormData] = useState({
    id: '',
    password: '',
    isIdEmpty: false,
    isPasswordEmpty: false
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [pwType, setPwType] = useState({
    type: 'password',
    visible: false
  });

  // 세션 정리
  useEffect(() => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('siteToken');
    sessionStorage.removeItem('refreshToken');
  }, []);

  // 로그인 성공 시 페이지 이동
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/safedb/SA/adminSites');
    }
  }, [isLoggedIn, navigate]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordType = () => {
    setPwType(prev => ({
      type: prev.visible ? 'password' : 'text',
      visible: !prev.visible
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { id, password } = formData;

    const isIdEmpty = !id;
    const isPasswordEmpty = !password;

    setFormData(prev => ({
      ...prev,
      isIdEmpty,
      isPasswordEmpty
    }));

    if (isIdEmpty || isPasswordEmpty) {
      return;
    }

    try {
      const response = await systemAdminLogin({
        username: id,
        password
      });

      if (response.success && response.token) {
        // 토큰 저장
        sessionStorage.setItem('token', response.token);
        sessionStorage.setItem('refreshToken', response.refreshToken || '');

        // 사용자 정보 저장
        if (response.user) {
          sessionStorage.setItem('user', JSON.stringify(response.user));
        }

        // SA 설정 적용
        const setNav = response.user?.userRole || 'SA';
        const setting = settings(setNav);

        setConfig('navvalue', setNav);
        setConfig('userAuth', setting.userAuth);
        setConfig('userId', id);
        setConfig('userAuth_K', setting.userAuth_K);
        setConfig('userName', response.user?.userName || '시스템 관리자');
        setConfig('checkorg', '');
        setConfig('checkorg_companyId', 'initech0');
        setConfig('navbarPosition', setting.navbarPosition);
        setConfig('firstLogin', setting.firstLogin);
        setConfig('userAvatar', avatarImg_SA);
        setConfig('isNavbarVerticalCollapsed', true);

        setIsLoggedIn(true);
      } else {
        alert('아이디 또는 비밀번호가 일치하지 않습니다. 다시 확인해주세요');
      }
    } catch (err) {
      alert('아이디 또는 비밀번호가 일치하지 않습니다. 다시 확인해주세요');
    }
  };

  return (
    <AuthCardLayout
      leftSideContent={
        <p className="text-white"></p>
      }
    >
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>
            ID<span className="text-danger">*</span>
          </Form.Label>
          <div style={{ position: 'relative' }}>
            <Form.Control
              placeholder="ID를 입력해 주세요."
              value={formData.id}
              name="id"
              onChange={handleFieldChange}
              type="text"
              className={formData.isIdEmpty ? 'is-invalid input-hover' : 'input-hover'}
              style={{
                paddingRight: '80px',
                backgroundPosition: formData.id
                  ? 'right calc(1em + 1.1rem) center'
                  : 'right calc(1em + 0rem) center'
              }}
            />
            {formData.id && (
              <span
                onClick={() => handleValueDelete('id', formData, setFormData)}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-14px)'
                }}
              >
                <AiFillCloseCircle />
              </span>
            )}
          </div>
          {formData.isIdEmpty && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              ID 입력이 필요합니다.
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3" style={{ position: 'relative' }}>
          <Form.Label>
            Password<span className="text-danger">*</span>
          </Form.Label>
          <div style={{ position: 'relative' }}>
            <Form.Control
              placeholder="비밀번호를 입력해주세요."
              value={formData.password}
              name="password"
              onChange={handleFieldChange}
              type={pwType.type}
              className={formData.isPasswordEmpty ? 'is-invalid input-hover' : 'input-hover'}
              style={{
                paddingRight: '80px',
                backgroundPosition: formData.password
                  ? 'right calc(1em + 2.6rem) center'
                  : 'right calc(1em + 0rem) center'
              }}
            />
            {formData.password && (
              <>
                <span
                  onClick={handlePasswordType}
                  style={{
                    cursor: 'pointer',
                    position: 'absolute',
                    right: '34px',
                    top: '50%',
                    transform: 'translateY(-14px)'
                  }}
                >
                  {pwType.visible ? <AiFillEye /> : <AiFillEyeInvisible />}
                </span>
                <span
                  onClick={() => handleValueDelete('password', formData, setFormData)}
                  style={{
                    cursor: 'pointer',
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-14px)'
                  }}
                >
                  <AiFillCloseCircle />
                </span>
              </>
            )}
          </div>
          {formData.isPasswordEmpty && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              비밀번호 입력이 필요합니다.
            </div>
          )}
        </Form.Group>

        <Form.Group>
          <Button type="submit" color="primary" className="mt-3 w-100">
            관리자 로그인
          </Button>
        </Form.Group>
      </Form>
    </AuthCardLayout>
  );
};

export default SystemAdminLogin;
